import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { groupTripApi } from '../../api/groupTripApi';
import {
  activityStateLabel,
  bookingCategoryLabel,
  continuationPresentation,
  disruptionStatement,
  formatActualDateTime,
  fulfillmentLabel,
  groupActualPath,
  isIdString,
  isPresentableDisruption,
  journeyPlanningPath,
  participationEndKindLabel,
  participationPresentation,
  participationStateText,
  phaseLabel,
  readErrorPresentation,
  travelerEventLabel,
  travelerStatusLabel,
} from './actualContinuationModel';
import { caseReference } from './disruptionModel';
import PersonalContinuationForm from './PersonalContinuationForm';
import './actual-continuation.css';

const list = (value) => (Array.isArray(value) ? value : []);

/** 旅伴實際行程：唯讀呈現後端 projection；只有本人可建立個人後續行程。 */
export default function TravelerActualPage() {
  const { groupTripId, travelerId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [navigationError, setNavigationError] = useState('');
  const generation = useRef(0);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const token = ++generation.current;
    if (!isIdString(groupTripId || '') || !isIdString(travelerId || '')) {
      setError(readErrorPresentation({ code: 95102 }));
      setPhase('error');
      return;
    }
    if (!quiet) setPhase('loading');
    setError(null);
    try {
      const payload = await groupTripApi.getTravelerActual(groupTripId, travelerId);
      if (token !== generation.current) return;
      setView(payload);
      setPhase('ready');
    } catch (cause) {
      if (token !== generation.current) return;
      if (quiet) return;
      setError(readErrorPresentation(cause));
      setPhase('error');
    }
  }, [groupTripId, travelerId]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  function handleCreated(result) {
    const path = journeyPlanningPath(result?.createdJourneyId);
    if (!path) {
      setNavigationError('個人後續行程已建立，但暫時無法開啟，請稍後重新整理此頁。');
      setFormOpen(false);
      load({ quiet: true });
      return;
    }
    navigate(path);
  }

  function handleAlreadyExists(presentation) {
    setFormOpen(false);
    setNotice(presentation.message);
    load({ quiet: true });
  }

  if (phase === 'loading') {
    return <main className="actual-view actual-view__state" aria-live="polite"><p>載入實際行程…</p></main>;
  }
  if (phase === 'error') {
    return (
      <main className="actual-view actual-view__state">
        <p className="actual-view__error" role="alert" data-error-kind={error?.kind}>{error?.message}</p>
        {error?.kind === 'network' || error?.kind === 'unexpected' ? (
          <button type="button" onClick={() => load()}>重新載入</button>
        ) : null}
      </main>
    );
  }

  const self = view?.viewerIsSelf === true;
  const traveler = view?.traveler || {};
  const participation = list(view?.activityParticipation);
  const titleById = new Map(participation.map((row) => [String(row.activityId), row.title]));
  const periods = list(view?.independentPeriods);
  const events = list(view?.runtimeEvents);
  const bookings = list(view?.bookings);
  const disruptions = list(view?.confirmedDisruptions).filter(isPresentableDisruption);
  const continuation = continuationPresentation(view);
  const end = view?.participationEnd;

  return (
    <main className="actual-view" data-testid="traveler-actual">
      <header className="actual-view__header">
        <p className="actual-view__eyebrow">{self ? '我的實際行程' : '旅伴實際行程'}</p>
        <h1>{self ? '我的實際行程' : (traveler.displayName || '旅伴')}</h1>
        {self && traveler.displayName ? <p className="actual-view__secondary">{traveler.displayName}</p> : null}
        <p className="actual-view__readonly" data-testid="actual-readonly">唯讀 · 依這趟揪團的旅途紀錄整理，無法在此修改</p>
        <nav className="actual-view__nav" aria-label="實際行程導覽">
          <Link to={groupActualPath(view?.groupTripId || groupTripId)}>團體實際行程</Link>
        </nav>
      </header>

      <section className="actual-view__panel" aria-labelledby="traveler-status-heading">
        <h2 id="traveler-status-heading">目前狀況</h2>
        <dl className="actual-view__facts">
          <div><dt>參與狀態</dt><dd data-testid="traveler-status">{travelerStatusLabel(traveler.status)}</dd></div>
          <div><dt>旅途階段</dt><dd>{view?.phase ? phaseLabel(view.phase) : '尚無法判定'}</dd></div>
          {traveler.effectiveJoinAt ? (
            <div><dt>中途加入生效</dt><dd data-testid="traveler-effective-join">{formatActualDateTime(traveler.effectiveJoinAt)}</dd></div>
          ) : null}
          <div>
            <dt>出發基準</dt>
            <dd>{view?.baseline ? `已於 ${formatActualDateTime(view.baseline.capturedAt)} 保存出發時的計畫` : '尚未保存出發時的計畫'}</dd>
          </div>
        </dl>
      </section>

      <section className="actual-view__panel" aria-labelledby="traveler-participation-heading">
        <h2 id="traveler-participation-heading">活動參加情形</h2>
        {participation.length === 0 ? <p className="actual-view__empty">目前沒有可顯示的活動參加情形。</p> : (
          <ol className="actual-view__list" data-testid="traveler-participation">
            {participation.map((row) => {
              const state = participationPresentation(row, traveler.status);
              return (
                <li key={row.activityId} className="actual-view__item" data-testid={`participation-${row.activityId}`}>
                  <p className="actual-view__item-title">
                    <span>{row.title || '活動資訊不完整'}</span>
                    {row.activityState && row.activityState !== 'SCHEDULED' ? (
                      <span className="actual-view__badge actual-badge--cancelled">{activityStateLabel(row.activityState)}</span>
                    ) : null}
                  </p>
                  <p>
                    <span className={`actual-view__badge${state.kind === 'state' ? '' : ` actual-badge--${state.kind}`}`} data-participation-kind={state.kind}>
                      {state.label}
                    </span>
                  </p>
                  {state.recorded ? <p className="actual-view__secondary">{state.recorded}</p> : null}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section className="actual-view__panel" aria-labelledby="traveler-periods-heading">
        <h2 id="traveler-periods-heading">獨立行動</h2>
        {periods.length === 0 ? <p className="actual-view__empty">沒有獨立行動紀錄。</p> : (
          <ul className="actual-view__list" data-testid="traveler-periods">
            {periods.map((period) => (
              <li key={period.periodId} className="actual-view__item">
                <p>{`${formatActualDateTime(period.startAt) || '時間未提供'} 起`}</p>
                <p className="actual-view__secondary">
                  {period.open || !period.endAt ? '尚未結束' : `${formatActualDateTime(period.endAt)} 結束`}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="actual-view__panel" aria-labelledby="traveler-end-heading">
        <h2 id="traveler-end-heading">團體參與結束</h2>
        {end ? (
          <p data-testid="traveler-participation-end">
            {`${participationEndKindLabel(end.kind)} · ${formatActualDateTime(end.endedAt) || '時間未提供'}`}
          </p>
        ) : <p className="actual-view__empty">沒有結束團體參與的紀錄。</p>}
      </section>

      <section className="actual-view__panel" aria-labelledby="traveler-events-heading">
        <h2 id="traveler-events-heading">旅途紀錄</h2>
        {events.length === 0 ? <p className="actual-view__empty">沒有旅途紀錄。</p> : (
          <ol className="actual-view__list" data-testid="traveler-events">
            {events.map((item) => (
              <li key={item.eventId} className="actual-view__item">
                <p className="actual-view__item-title">
                  <span>{travelerEventLabel(item.eventType)}</span>
                  {item.activityId ? <span>{titleById.get(String(item.activityId)) || ''}</span> : null}
                </p>
                <p className="actual-view__secondary">{formatActualDateTime(item.occurredAt) || '時間未提供'}</p>
                {item.fromState || item.toState ? (
                  <p>{`${participationStateText(item.fromState) || '未記錄'} → ${participationStateText(item.toState) || '未記錄'}`}</p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="actual-view__panel" aria-labelledby="traveler-bookings-heading">
        <h2 id="traveler-bookings-heading">相關預訂</h2>
        {bookings.length === 0 ? <p className="actual-view__empty">沒有相關預訂。</p> : (
          <ul className="actual-view__list" data-testid="traveler-bookings">
            {bookings.map((booking) => (
              <li key={booking.bookingId} className="actual-view__item">
                <p className="actual-view__item-title">
                  <span>{bookingCategoryLabel(booking.category)}</span>
                  <span className="actual-view__badge">{fulfillmentLabel(booking.fulfillmentStatus)}</span>
                </p>
                {booking.seatOrBed ? <p className="actual-view__secondary">{`座位／床位：${booking.seatOrBed}`}</p> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="actual-view__panel" aria-labelledby="traveler-disruptions-heading">
        <h2 id="traveler-disruptions-heading">已確認的行程異動</h2>
        {disruptions.length === 0 ? <p className="actual-view__empty">目前沒有已確認的行程異動。</p> : (
          <ul className="actual-view__list" data-testid="traveler-disruptions">
            {disruptions.map((item) => {
              const statement = disruptionStatement(item, { viewerIsSelf: self });
              const affected = item.travelerConfirmedAffected === true && item.impactScope === 'INDIVIDUAL_ONLY';
              return (
                <li key={`${item.caseId}-${item.bookingId || ''}-${item.impactScope}`} className="actual-view__item">
                  <p>
                    <span className={`actual-view__badge${affected ? ' actual-badge--affected' : ''}`}>{statement}</span>
                  </p>
                  <p className="actual-view__secondary">
                    {[caseReference(item.caseId), item.confirmedAt ? `確認於 ${formatActualDateTime(item.confirmedAt)}` : '']
                      .filter(Boolean).join(' · ')}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {continuation.mode !== 'hidden' ? (
        <section className="actual-view__panel" aria-labelledby="traveler-continuation-heading" data-testid="continuation-section">
          <h2 id="traveler-continuation-heading">個人後續行程</h2>
          {notice ? <p className="actual-view__notice" role="status" data-testid="continuation-notice">{notice}</p> : null}
          {notice && continuation.mode !== 'exists' ? (
            <button type="button" className="actual-view__secondary-button" onClick={() => load({ quiet: true })}>
              重新整理以查看既有行程
            </button>
          ) : null}
          {navigationError ? <p className="actual-view__error" role="alert">{navigationError}</p> : null}
          {continuation.mode === 'exists' ? (
            <>
              <p>{continuation.startedAt ? `你已建立個人後續行程，從 ${continuation.startedAt} 開始。` : '你已建立個人後續行程。'}</p>
              <p className="actual-view__secondary">這份行程獨立於揪團，不會與揪團同步。</p>
              {continuation.journeyPath ? (
                <Link className="actual-view__link" to={continuation.journeyPath}>前往我的個人後續行程</Link>
              ) : null}
            </>
          ) : null}
          {continuation.mode === 'blocked' ? (
            <p className="actual-view__empty">這趟揪團已取消或正在取消，無法建立個人後續行程。</p>
          ) : null}
          {continuation.mode === 'available' && !notice ? (
            formOpen ? (
              <PersonalContinuationForm
                groupTripId={view.groupTripId || groupTripId}
                travelerId={traveler.travelerId || travelerId}
                effectiveJoinAt={traveler.effectiveJoinAt || null}
                onCreated={handleCreated}
                onAlreadyExists={handleAlreadyExists}
                onCancel={() => setFormOpen(false)}
              />
            ) : (
              <>
                <p className="actual-view__secondary">你可以另外建立一份屬於自己的獨立行程，安排與揪團分開的後續旅程。</p>
                <button type="button" onClick={() => setFormOpen(true)}>建立個人後續行程</button>
              </>
            )
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
